import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Check, LayoutGrid, ListPlus, MoreHorizontal, Pencil, RefreshCw, RotateCcw, Target } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/app/AuthProvider';
import { useIsMobile } from '@/lib/useMediaQuery';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { PageHeader } from '@/components/layout/PageHeader';
import { buildCatalog, getStaticSource, isSourcePermitted, requiredDatasets, widgetSourceIds, type Viewer } from '@/features/intelligence/dashboard/catalog';
import type { DatasetId } from '@/features/intelligence/dashboard/datasets';
import type { ForecastSettings } from '@/features/intelligence/dashboard/evaluate';
import {
  addSection,
  addWidget,
  blankWidget,
  duplicateWidget,
  findWidget,
  moveSection,
  moveWidget,
  nudgeWidget,
  removeSection,
  removeWidget,
  replaceWidget,
  resizeWidget,
  updateSection,
} from '@/features/intelligence/dashboard/layout';
import { useDashboardLayout, type SaveStatus } from '@/features/intelligence/dashboard/persistence';
import type { DashboardWindow } from '@/features/intelligence/dashboard/range';
import type { DashboardLayout, DashboardPageKey, DashboardWidget, WidgetKind } from '@/features/intelligence/dashboard/types';
import { useDashboardData } from '@/features/intelligence/dashboard/useDashboardData';
import { AddWidgetMenu } from './AddWidgetMenu';
import { DashboardContext, type DashboardContextValue } from './DashboardContext';
import { DashboardSectionView, type DropTarget, type SectionHandlers } from './DashboardSectionView';
import { TrackedKpisDialog } from './TrackedKpisDialog';
import { WidgetEditor } from './WidgetEditor';

export interface DashboardBoardProps {
  page: DashboardPageKey;
  title: string;
  eyebrow: ReactNode;
  window: DashboardWindow;
  forecast: ForecastSettings;
  buildDefault: (permitted: (sourceId: string) => boolean) => DashboardLayout;
  /** Range / horizon pickers rendered in the header while viewing. */
  controls?: ReactNode;
  /** Widget kinds listed first in the add menu. */
  featuredKinds?: WidgetKind[];
  /** Header meta line while viewing; defaults to the window label. */
  subtitle?: string;
}

interface EditorState {
  widget: DashboardWidget;
  sectionId: string;
  isNew: boolean;
}

interface ConfirmState {
  title: string;
  detail: string;
  actionLabel: string;
  action: () => void;
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function SaveStatusLabel({ status, error }: { status: SaveStatus; error: string | null }) {
  if (status === 'dirty' || status === 'saving') return <span className="text-xs text-muted-foreground">Saving…</span>;
  if (status === 'saved')
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Check className="h-3.5 w-3.5" /> Saved
      </span>
    );
  if (status === 'error') return <span className="text-xs text-fresh-salmon-dark">Couldn't save{error ? ` — ${error}` : ''}</span>;
  return null;
}

function BoardSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-6 w-48" />
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-[192px]" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Skeleton className="h-[400px] md:col-span-2" />
        <Skeleton className="h-[400px]" />
      </div>
    </div>
  );
}

export function DashboardBoard({ page, title, eyebrow, window: win, forecast, buildDefault, controls, featuredKinds, subtitle }: DashboardBoardProps) {
  const { role, profile } = useAuth();
  const sectionAccess = profile?.section_access ?? null;
  const viewer = useMemo<Viewer>(() => ({ role, sectionAccess }), [role, sectionAccess]);
  const isMobile = useIsMobile();

  const permitted = useCallback(
    (sourceId: string) => {
      const def = getStaticSource(sourceId);
      return !!def && isSourcePermitted(def, viewer);
    },
    [viewer],
  );
  const buildDefaultLayout = useCallback(() => buildDefault(permitted), [buildDefault, permitted]);
  const state = useDashboardLayout(page, buildDefaultLayout, { enabled: role !== null });
  const { layout, setLayout, editing } = state;

  const layoutRef = useRef<DashboardLayout | null>(layout);
  layoutRef.current = layout;

  // --- Editor -------------------------------------------------------------
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [draftSources, setDraftSources] = useState<string[]>([]);
  const onDraftChange = useCallback((draft: DashboardWidget) => {
    const ids = widgetSourceIds(draft).filter(Boolean);
    setDraftSources((prev) => (sameList(prev, ids) ? prev : ids));
  }, []);
  useEffect(() => {
    if (!editor) setDraftSources((prev) => (prev.length ? [] : prev));
  }, [editor]);

  // --- Data ---------------------------------------------------------------
  const extra = useMemo(() => {
    const ids = requiredDatasets(draftSources, viewer);
    ids.add('kpi_tracked');
    return Array.from(ids) as DatasetId[];
  }, [draftSources, viewer]);
  const data = useDashboardData(layout, win, viewer, extra);
  const catalog = useMemo(() => buildCatalog(data.bag, viewer), [data.bag, viewer]);

  const context = useMemo<DashboardContextValue>(
    () => ({ page, bag: data.bag, catalog, window: win, forecast, pending: data.pending, errors: data.errors, editing, isMobile }),
    [page, data.bag, data.pending, data.errors, catalog, win, forecast, editing, isMobile],
  );

  // --- Dialogs ------------------------------------------------------------
  const [kpisOpen, setKpisOpen] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  // --- Drag and drop ------------------------------------------------------
  const [dragWidgetId, setDragWidgetId] = useState<string | null>(null);
  const [dropTarget, setDropTargetState] = useState<DropTarget | null>(null);
  const dragRef = useRef<string | null>(null);
  const dropRef = useRef<DropTarget | null>(null);

  const setDropTarget = useCallback((next: DropTarget | null) => {
    const prev = dropRef.current;
    if (prev === next || (prev && next && prev.sectionId === next.sectionId && prev.index === next.index)) return;
    dropRef.current = next;
    setDropTargetState(next);
  }, []);

  const clearDrag = useCallback(() => {
    dragRef.current = null;
    setDragWidgetId(null);
    setDropTarget(null);
  }, [setDropTarget]);

  const openCreate = useCallback(
    (kind: WidgetKind, sectionId?: string) => {
      const current = layoutRef.current;
      if (!current) return;
      let target = sectionId ?? current.sections[current.sections.length - 1]?.id;
      if (!target) {
        const created = addSection(current, 'Overview');
        setLayout(created.layout);
        target = created.id;
      }
      setEditor({ widget: blankWidget(kind), sectionId: target, isNew: true });
    },
    [setLayout],
  );

  const handlers = useMemo<SectionHandlers>(
    () => ({
      onEdit: (widgetId) => {
        const loc = layoutRef.current ? findWidget(layoutRef.current, widgetId) : null;
        if (loc) setEditor({ widget: loc.widget, sectionId: loc.section.id, isNew: false });
      },
      onDuplicate: (widgetId) => setLayout((prev) => duplicateWidget(prev, widgetId)),
      onRemove: (widgetId) => {
        const before = layoutRef.current;
        setLayout((prev) => removeWidget(prev, widgetId));
        toast('Widget removed', {
          action: before ? { label: 'Undo', onClick: () => setLayout(before) } : undefined,
        });
      },
      onNudge: (widgetId, direction) => setLayout((prev) => nudgeWidget(prev, widgetId, direction)),
      onResize: (widgetId, w, h) => setLayout((prev) => resizeWidget(prev, widgetId, w, h)),
      onDragStart: (widgetId) => {
        dragRef.current = widgetId;
        setDragWidgetId(widgetId);
      },
      onDragEnd: clearDrag,
      onDragOverWidget: (event: DragEvent, sectionId, index) => {
        if (!dragRef.current) return;
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = 'move';
        const rect = event.currentTarget.getBoundingClientRect();
        const after = event.clientX > rect.left + rect.width / 2;
        setDropTarget({ sectionId, index: after ? index + 1 : index });
      },
      onDragOverSection: (event: DragEvent, sectionId, endIndex) => {
        if (!dragRef.current) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDropTarget({ sectionId, index: endIndex });
      },
      onDrop: (event: DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        const widgetId = dragRef.current;
        const target = dropRef.current;
        if (widgetId && target) setLayout((prev) => moveWidget(prev, widgetId, target.sectionId, target.index));
        clearDrag();
      },
      onAddWidget: (kind, sectionId) => openCreate(kind, sectionId),
      onRenameSection: (sectionId, title) => setLayout((prev) => updateSection(prev, sectionId, { title })),
      onDescribeSection: (sectionId, description) => setLayout((prev) => updateSection(prev, sectionId, { description: description || undefined })),
      onToggleCollapse: (sectionId) =>
        setLayout((prev) => {
          const section = prev.sections.find((s) => s.id === sectionId);
          return updateSection(prev, sectionId, { collapsed: section?.collapsed ? undefined : true });
        }),
      onMoveSection: (sectionId, direction) => setLayout((prev) => moveSection(prev, sectionId, direction)),
      onRemoveSection: (sectionId) => {
        const section = layoutRef.current?.sections.find((s) => s.id === sectionId);
        if (!section) return;
        const remove = () => setLayout((prev) => removeSection(prev, sectionId));
        if (section.widgets.length === 0) {
          remove();
          return;
        }
        setConfirm({
          title: `Remove “${section.title}”?`,
          detail: `Its ${section.widgets.length} ${section.widgets.length === 1 ? 'widget' : 'widgets'} will be removed too. You can discard your changes to get them back.`,
          actionLabel: 'Remove section',
          action: remove,
        });
      },
    }),
    [setLayout, clearDrag, setDropTarget, openCreate],
  );

  const saveWidget = useCallback(
    (widget: DashboardWidget) => {
      const current = editor;
      if (!current) return;
      setLayout((prev) => (current.isNew ? addWidget(prev, current.sectionId, widget) : replaceWidget(prev, widget)));
      setEditor(null);
    },
    [editor, setLayout],
  );

  const addNewSection = useCallback(() => {
    if (!editing) state.startEditing();
    setLayout((prev) => addSection(prev, 'New section').layout);
  }, [editing, state, setLayout]);

  const requestReset = () =>
    setConfirm({
      title: 'Reset to the default layout?',
      detail: 'Your sections, widgets and sizes on this page will be replaced with the starting layout for your role.',
      actionLabel: 'Reset layout',
      action: state.resetToDefault,
    });

  const isEmpty = !!layout && layout.sections.length === 0;

  const actions = editing ? (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <SaveStatusLabel status={state.status} error={state.saveError} />
      <AddWidgetMenu label="Add widget" featured={featuredKinds} onPick={(kind) => openCreate(kind)} disabled={!layout} />
      <Button variant="outline" size="sm" onClick={addNewSection}>
        <ListPlus className="h-4 w-4" /> Add section
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" className="h-8 w-8" aria-label="More layout options">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onSelect={() => setKpisOpen(true)}>
            <Target className="mr-2 h-4 w-4" /> Tracked KPIs…
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={requestReset}>
            <RotateCcw className="mr-2 h-4 w-4" /> Reset to default layout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button variant="ghost" size="sm" onClick={state.discardEditing}>
        Discard
      </Button>
      <Button size="sm" onClick={state.finishEditing}>
        Done
      </Button>
    </div>
  ) : (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {controls}
      <Button variant="outline" size="sm" onClick={state.startEditing} disabled={!layout}>
        <Pencil className="h-4 w-4" /> Customize
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" className="h-8 w-8" aria-label="More options">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onSelect={() => setKpisOpen(true)}>
            <Target className="mr-2 h-4 w-4" /> Tracked KPIs…
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => data.refetch()} disabled={data.isFetching}>
            <RefreshCw className={cn('mr-2 h-4 w-4', data.isFetching && 'animate-spin')} /> Refresh data
          </DropdownMenuItem>
          {state.isCustomized && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={requestReset}>
                <RotateCcw className="mr-2 h-4 w-4" /> Reset to default layout
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const meta = editing
    ? isMobile
      ? 'Customizing — use each card’s menu to edit, move or resize it.'
      : 'Customizing — drag cards to reorder, pull a corner to resize, double-click to edit.'
    : `${subtitle ?? win.label}${state.isCustomized ? ' · your layout' : ''}`;

  return (
    <DashboardContext.Provider value={context}>
      <div className={cn('space-y-8', editing && 'pb-24')}>
        <PageHeader title={title} eyebrow={eyebrow} meta={meta} actions={actions} />

        {state.isLoading || !layout ? (
          <BoardSkeleton />
        ) : isEmpty ? (
          <EmptyState
            icon={LayoutGrid}
            title="This dashboard is empty"
            detail="Start from the default layout for your role, or build your own section by section."
            action={
              <div className="flex flex-wrap justify-center gap-2">
                <Button variant="outline" onClick={state.resetToDefault}>
                  <RotateCcw className="h-4 w-4" /> Use the default layout
                </Button>
                <Button onClick={addNewSection}>
                  <ListPlus className="h-4 w-4" /> Add a section
                </Button>
              </div>
            }
          />
        ) : (
          layout.sections.map((section, index) => (
            <DashboardSectionView
              key={section.id}
              section={section}
              isFirst={index === 0}
              isLast={index === layout.sections.length - 1}
              dragWidgetId={dragWidgetId}
              dropTarget={dropTarget}
              handlers={handlers}
            />
          ))
        )}
      </div>

      <WidgetEditor
        open={editor !== null}
        widget={editor?.widget ?? null}
        isNew={editor?.isNew ?? false}
        onSave={saveWidget}
        onRemove={(id) => {
          handlers.onRemove(id);
          setEditor(null);
        }}
        onClose={() => setEditor(null)}
        onDraftChange={onDraftChange}
      />

      <TrackedKpisDialog open={kpisOpen} onOpenChange={setKpisOpen} />

      <Dialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{confirm?.title}</DialogTitle>
            <DialogDescription>{confirm?.detail}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                confirm?.action();
                setConfirm(null);
              }}
            >
              {confirm?.actionLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardContext.Provider>
  );
}
